import * as Sentry from "@sentry/react";

function getEnvironment(): string {
  const version = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
  if (version === "dev") return "development";
  if (version.includes("-rc")) return "staging";
  return "production";
}

/** Session Replay records a masked page: no text, no input values, no images or media. */
export const REPLAY_PRIVACY = { maskAllText: true, maskAllInputs: true, blockAllMedia: true } as const;

/**
 * Replay options. ``stickySession: false`` keeps the replay session in memory
 * only: the SDK otherwise writes ``sentryReplaySession`` to sessionStorage,
 * which is device storage that is not strictly necessary (privacy policy §10).
 */
export const REPLAY_OPTIONS = { ...REPLAY_PRIVACY, stickySession: false } as const;

export const initSentry = (dsn: string) => {
  const environment = getEnvironment();
  const release = typeof __APP_VERSION__ !== "undefined" && __APP_VERSION__ !== "dev"
    ? __APP_VERSION__
    : undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    // No sendDefaultPii: the privacy policy (§6) promises error, page/action, browser, IP and
    // user id only — no email, cookies or request bodies.
    enableLogs: true,
    tracesSampleRate: environment === "development" ? 0 : 1.0,
    // Browser profiling — requires the ``Document-Policy: js-profiling``
    // response header set in ``tenant-frontend/public/_headers``.
    profilesSampleRate: environment === "development" ? 0 : 1.0,
    // Replay only when an error occurs, never for ordinary sessions (privacy policy §6).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: environment === "development" ? 0 : 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      ...(environment !== "development" ? [Sentry.replayIntegration(REPLAY_OPTIONS)] : []),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    beforeSend(event) {
      if (environment === "development") {
        console.log("[Sentry] Event:", event.type || "error");
      }
      return event;
    },
  });
};

export const setTenantUser = (user: { id: string; tenant_name?: string } | null) => {
  if (user) {
    // The user id only: no email in Sentry (privacy policy §6).
    Sentry.setUser({ id: user.id });
    if (user.tenant_name) {
      Sentry.setTag("tenant", user.tenant_name);
    }
  } else {
    Sentry.setUser(null);
  }
};

export const captureException = (error: unknown, context?: Record<string, unknown>) => {
  Sentry.captureException(error, {
    contexts: context ? { additionalContext: context } : undefined,
  });
};

export default Sentry;
